import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import * as idp from './idpClient'
import { AuthContext, type AuthContextValue, type AuthStatus } from './context'
import {
  clearSession,
  isExpiring,
  onSessionChanged,
  readSession,
  toStoredSession,
  withRefreshLock,
  writeSession,
  type StoredSession,
} from './tokenStore'
import { rememberFederatedLogin } from './federatedState'
import { IdpError, type FederatedProvider, type Session, type TokenResponse } from './types'

/**
 * Owns the signed-in session: restores it on load, keeps the access token
 * fresh, and exposes sign-in and sign-out.
 *
 * This replaces `Auth0Provider`. The portal talks to the Passgenau IdP's
 * backend-facilitated endpoints directly, so there is no vendor SDK and no
 * hidden iframe — the whole flow is the four calls in `idpClient`.
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [stored, setStored] = useState<StoredSession | undefined>(undefined)
  const [session, setSession] = useState<Session | undefined>(undefined)
  const [error, setError] = useState<Error | undefined>(undefined)

  /**
   * The live session, readable from callbacks without making them depend on
   * render state. `getAccessToken` is called from query functions that must not
   * be re-created on every token change.
   */
  const storedRef = useRef<StoredSession | undefined>(undefined)
  const setStoredSession = useCallback((next: StoredSession | undefined) => {
    storedRef.current = next
    setStored(next)
  }, [])

  const signOutLocally = useCallback(() => {
    clearSession()
    setStoredSession(undefined)
    setSession(undefined)
    setStatus('unauthenticated')
  }, [setStoredSession])

  /**
   * Exchange the refresh token for a new access token.
   *
   * Held under the cross-tab lock, and re-reads storage inside it: another tab
   * may have rotated the token while we waited, in which case its result is
   * already the current one and presenting ours would revoke the chain.
   */
  const refresh = useCallback(async (): Promise<StoredSession | undefined> => {
    return withRefreshLock(async () => {
      const current = readSession()
      if (!current) return undefined
      if (!isExpiring(current)) {
        setStoredSession(current)
        return current
      }
      if (!current.refreshToken) return undefined

      const tokens = await idp.refreshTokens(current.refreshToken)
      const next = toStoredSession(tokens, current.refreshToken)
      writeSession(next)
      setStoredSession(next)
      return next
    })
  }, [setStoredSession])

  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    const current = storedRef.current ?? readSession()
    if (!current) return undefined
    if (!isExpiring(current)) return current.accessToken

    try {
      return (await refresh())?.accessToken
    } catch (cause) {
      // A rejected refresh means the chain is gone — rotated by someone else,
      // expired, or revoked. There is no silent recovery; sign in again.
      if (cause instanceof IdpError && cause.isUnauthenticated) {
        signOutLocally()
        return undefined
      }
      throw cause
    }
  }, [refresh, signOutLocally])

  /** Load the identity for a session we already hold tokens for. */
  const loadSession = useCallback(
    async (current: StoredSession) => {
      const profile = await idp.getSession(current.accessToken)
      setSession(profile)
      setStatus('authenticated')
      setError(undefined)
    },
    [setSession]
  )

  const completeLogin = useCallback(
    async (tokens: TokenResponse) => {
      const next = toStoredSession(tokens)
      writeSession(next)
      setStoredSession(next)
      await loadSession(next)
    },
    [loadSession, setStoredSession]
  )

  const startFederatedLogin = useCallback(
    async (
      provider: FederatedProvider,
      options: { connectionId?: string | null; loginHint?: string; returnTo?: string }
    ) => {
      const { url, state } = await idp.getConsentUrl(provider, options)

      // The callback lands on a route that only knows the code and the state.
      // Which provider to exchange with, and where the person was heading, are
      // ours to remember.
      rememberFederatedLogin(state, { provider, returnTo: options.returnTo })
      window.location.assign(url)
    },
    []
  )

  const logout = useCallback(async () => {
    const current = storedRef.current
    if (current) {
      try {
        await idp.endSession(current.accessToken, current.refreshToken)
      } catch (cause) {
        // The local session is going away regardless. A failed revoke is worth
        // recording but must not strand the person on a signed-in-looking page.
        console.warn('Could not revoke the session with the identity service', cause)
      }
    }
    signOutLocally()
  }, [signOutLocally])

  // Restore on load: refresh first if the stored token is stale, then confirm
  // it with the service rather than trusting a client-side decode.
  useEffect(() => {
    let cancelled = false

    const restore = async () => {
      const current = readSession()
      if (!current) {
        setStatus('unauthenticated')
        return
      }
      setStoredSession(current)

      try {
        const usable = isExpiring(current) ? await refresh() : current
        if (cancelled) return
        if (!usable) {
          signOutLocally()
          return
        }
        await loadSession(usable)
      } catch (cause) {
        if (cancelled) return
        if (cause instanceof IdpError && cause.isUnauthenticated) {
          signOutLocally()
          return
        }
        // The service is unreachable or broken. Report it rather than bouncing
        // the person to a sign-in page that will fail the same way.
        setError(cause instanceof Error ? cause : new Error(String(cause)))
        setStatus('unauthenticated')
      }
    }

    void restore()
    return () => {
      cancelled = true
    }
  }, [loadSession, refresh, setStoredSession, signOutLocally])

  // Keep the access token ahead of its expiry while the tab is open, so an
  // in-flight query is never the thing that discovers the token has died.
  useEffect(() => {
    if (status !== 'authenticated' || !stored) return

    const delay = Math.max(stored.expiresAt - Date.now() - 60_000, 5_000)
    const timer = window.setTimeout(() => {
      void getAccessToken()
    }, delay)

    return () => window.clearTimeout(timer)
  }, [status, stored, getAccessToken])

  // Follow sign-in and sign-out in other tabs.
  useEffect(
    () =>
      onSessionChanged(next => {
        if (!next) {
          if (storedRef.current) signOutLocally()
          return
        }
        if (next.accessToken === storedRef.current?.accessToken) return

        setStoredSession(next)
        void loadSession(next).catch(() => signOutLocally())
      }),
    [loadSession, setStoredSession, signOutLocally]
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      accessToken: status === 'authenticated' ? stored?.accessToken : undefined,
      idToken: status === 'authenticated' ? stored?.idToken : undefined,
      session,
      error,
      completeLogin,
      startFederatedLogin,
      logout,
      getAccessToken,
    }),
    [status, stored, session, error, completeLogin, startFederatedLogin, logout, getAccessToken]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
